import { Product, Session } from 'src/funnels/common/types';
import { QuizAnswer } from 'src/funnels/common/types/quiz';
import { API_HOST_MANIFESTEEM } from 'common/config/constants';
import { authorizedFetch, RequestOptions } from 'common/utils/http';
import { User } from 'common/types/api';
import { setCrossSubdomainCookie } from 'common/utils/cookies';

type CustomersBodyType = {
  id?: string;
  isTestUser?: boolean;
  email?: string;
};

export type PageViewType = {
  timestamp: number;
  url: string;
};

export type OneClickPaymentConfigType = {
  productId: string;
  userId: string;
};

export type UpdateUserPayloadType = {
  email?: string;
  abcd?: string;
  onboardingFinished?: boolean;
  purchaseUrl?: string;
  quiz?: {
    answers: QuizAnswer[];
  };
  mailProperties?: {
    marketingCheckbox: boolean;
    funnelsEnvironment: string;
  };
  socialParameters?: {
    fbp?: string;
    fbc?: string;
    gclid?: string;
  };
  onbCompeted?: {
    timestamp: number;
    url: string;
  };
  pageViews?: PageViewType[];
  recommendedPlanId?: string;
};

export type UpdateUserPageviewsType = {
  pageViews: PageViewType[];
};

export type SaveAbTestsPayloadType = {
  userId: string;
  value: string;
  timestamp: number;
  name: string;
};

class Server {
  async getUserById(id: string): Promise<any | null> {
    try {
      const body: CustomersBodyType = {
        id,
      };

      const res = await fetch(
        `${API_HOST_MANIFESTEEM}/api/payments/customers/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        },
      );

      if (!res.ok) {
        console.error(`Failed to fetch user with id ${id}`);
        return null;
      }

      const response = await res.json();

      return response.data;
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  async getUser(options?: RequestOptions) {
    const { res } = await authorizedFetch<User>(
      {
        url: 'users/me',
        method: 'GET',
      },
      options,
    );

    if (res) {
      const userDataString = JSON.stringify(res);
      setCrossSubdomainCookie('userData', userDataString, 7);
    }

    return res;
  }

  async createUserOrGetIfExist(data: CustomersBodyType): Promise<any | null> {
    const { id, isTestUser, email } = data;

    try {
      const body: CustomersBodyType = {
        ...(id && {
          id,
        }),
        ...(isTestUser && {
          isTestUser,
        }),
        ...(email && {
          email,
        }),
      };

      const res = await fetch(
        `${API_HOST_MANIFESTEEM}/api/payments/customers/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...body,
            application: 'Manifesteem', // Server supports a separate frontend, so it requires an identifier for the application that initiated the user
          }),
        },
      );

      if (!res.ok) {
        console.error(`Failed to fetch user with id ${id}`);
        return null;
      }

      const response = await res.json();

      return response.data;
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  async updateUserById(
    id: string,
    payload: UpdateUserPayloadType,
  ): Promise<Record<string, any> | null> {
    if (!id) {
      return null;
    }

    const modifiedPeyload = { ...payload };
    delete modifiedPeyload.abcd;

    if (payload.abcd) {
      modifiedPeyload.email = payload.abcd;
    }

    try {
      const res = await fetch(
        `${API_HOST_MANIFESTEEM}/api/payments/customers/${id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(modifiedPeyload),
        },
      );

      if (!res.ok) {
        console.error(`Failed to patch user with id ${id}`);
        return null;
      }

      return await res.json();
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  async savePageViewsByUserId(
    id: string,
    payload: UpdateUserPageviewsType,
  ): Promise<Record<string, any> | null> {
    if (!id) {
      return null;
    }

    const user = await this.getUserById(id);

    if (!user) return null;

    const existingPageviews = user.pageViews;

    const newPageviews = [...existingPageviews, ...payload.pageViews];

    const body = { pageViews: newPageviews };

    try {
      const res = await fetch(
        `${API_HOST_MANIFESTEEM}/api/payments/customers/${id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        },
      );

      if (!res.ok) {
        console.error(`Failed to patch user with id ${id}`);
        return null;
      }

      return await res.json();
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  async saveAbTests(
    payload: SaveAbTestsPayloadType[],
  ): Promise<Record<string, any> | null> {
    try {
      const res = await fetch(
        `${API_HOST_MANIFESTEEM}/api/analytics/ab-tests`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );

      if (!res.ok) {
        console.error(`Failed to save ab tests`);
        return null;
      }
      return await res.json();
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  async getProductPlans(): Promise<any | null> {
    try {
      const res = await fetch(
        `${API_HOST_MANIFESTEEM}/api/payments/products/`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (!res.ok) {
        console.error(`Failed to fetch product plans`);
        return null;
      }

      const contentType = res.headers.get('Content-Type');
      if (contentType && contentType.includes('application/json')) {
        const response = await res.json();
        return response.data;
      } else {
        console.error('Unexpected content type:', contentType);
        return null;
      }
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  async initiateOneClickPayment(
    payload: OneClickPaymentConfigType,
  ): Promise<any | null> {
    try {
      const res = await fetch(
        `${API_HOST_MANIFESTEEM}/api/payments/orders/one-click/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );

      if (!res.ok) {
        console.error(`Failed to fetch product plans`);
        return null;
      }

      const response = await res.json();

      return response.data;
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  async savePdfToUser(id: string, pdf: Product[]): Promise<any | null> {
    try {
      const res = await fetch(
        `${API_HOST_MANIFESTEEM}/api/payments/customers/${id}/pdfs`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(pdf),
        },
      );

      if (!res.ok) {
        console.error(`Failed to fetch user with id ${id}`);
        return null;
      }

      const response = await res.json();

      return response.data;
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  async saveSessionToUser(id: string, session: Session): Promise<any | null> {
    try {
      const res = await fetch(
        `${API_HOST_MANIFESTEEM}/api/payments/customers/${id}/sessions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(session),
        },
      );

      if (!res.ok) {
        console.error(`Failed to fetch user with id ${id}`);
        return null;
      }

      const response = await res.json();

      return response.data;
    } catch (error) {
      console.error(error);
    }

    return null;
  }
}

export const server = new Server();
